import { algoliasearch } from "algoliasearch";
import { functions } from "@app/configs/firebase.js";
import { httpsCallable } from "firebase/functions";
import { collection, doc as fsDoc, getDoc } from "firebase/firestore";
import db from "@app/configs/firestore.js";
import { authState } from "@app/store/authStore.svelte.js";
import { canUserAccessFolder as canUserAccessFolderWithUser } from "./explorerFolderAccess.js";

const KEY_REFRESH_BUFFER_MS = 5 * 60 * 1000;
const DEFAULT_HITS_PER_PAGE = 40;

let cachedKeyInfo = null;
let searchClients = new Map();

function shouldRefreshKey() {
	if (!cachedKeyInfo?.apiKey || !cachedKeyInfo?.validUntil) {
		return true;
	}

	return cachedKeyInfo.validUntil * 1000 - Date.now() < KEY_REFRESH_BUFFER_MS;
}

async function getKeyInfo() {
	if (shouldRefreshKey()) {
		const result = await httpsCallable(functions, "v8GetStorageSearchKey")();
		cachedKeyInfo = result.data;
		searchClients = new Map((cachedKeyInfo.searchKeys || [{
			scope: "personal",
			apiKey: cachedKeyInfo.apiKey
		}]).map((keyInfo) => [
			keyInfo.scope,
			algoliasearch(cachedKeyInfo.appId, keyInfo.apiKey)
		]));
	}

	return cachedKeyInfo;
}

function quoteFilterValue(value) {
	return `"${String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function buildSearchFilters(filters = {}) {
	const parts = [];

	if (filters.starredOnly) {
		parts.push("star:true");
	}

	if (filters.type && filters.type !== "all") {
		parts.push(`type:${quoteFilterValue(filters.type)}`);
	}

	if (filters.createdFrom) {
		parts.push(`createdOn >= ${new Date(filters.createdFrom).getTime()}`);
	}

	if (filters.createdTo) {
		parts.push(`createdOn <= ${endOfDay(filters.createdTo)}`);
	}

	if (filters.updatedFrom) {
		parts.push(`updatedOn >= ${new Date(filters.updatedFrom).getTime()}`);
	}

	if (filters.updatedTo) {
		parts.push(`updatedOn <= ${endOfDay(filters.updatedTo)}`);
	}

	return parts.join(" AND ");
}

function endOfDay(dateValue) {
	const date = new Date(dateValue);
	date.setHours(23, 59, 59, 999);
	return date.getTime();
}

function mapHitToRecord(hit) {
	return {
		...hit,
		id: hit.id,
		rootDir: hit.rootDir || (hit.scope === "team" ? "team" : "personal"),
		createdOnMillis: hit.createdOn || null,
		updatedOnMillis: hit.updatedOn || null,
		createdOn: hit.createdOn || null,
		updatedOn: hit.updatedOn || null
	};
}

export function canUserAccessFolder(folder) {
	return canUserAccessFolderWithUser(folder, authState.user);
}

async function getTeamParentFolders(records) {
	const teamId = authState.team?.id;
	const locationIds = [
		...new Set(records
			.filter((record) => record.rootDir === "team" && record.type !== "folder" && record.locationId)
			.map((record) => record.locationId))
	];

	if (!teamId || locationIds.length === 0) {
		return new Map();
	}

	const teamDbRef = collection(db, "teams", teamId, "storage");
	const folderEntries = await Promise.all(locationIds.map(async (folderId) => {
		try {
			const doc = await getDoc(fsDoc(teamDbRef, folderId));
			return [folderId, doc.exists() ? doc.data() : null];
		} catch (err) {
			console.error("Error checking parent folder permissions:", err);
			return [folderId, null];
		}
	}));

	return new Map(folderEntries);
}

export async function filterStorageSearchRecords(records) {
	const parentFolders = await getTeamParentFolders(records);

	return records.filter((record) => {
		if (record.rootDir === "personal") {
			return true;
		}

		if (record.type === "folder") {
			return canUserAccessFolder(record);
		}

		if (!record.locationId) {
			return true;
		}

		const parentFolder = parentFolders.get(record.locationId);
		return parentFolder ? canUserAccessFolder(parentFolder) : false;
	});
}

function getSearchScopes(keyInfo, requestedScope) {
	const availableScopes = (keyInfo.searchKeys || [{
		scope: "personal",
		apiKey: keyInfo.apiKey
	}]).map((keyInfo) => keyInfo.scope);

	if (requestedScope === "personal" || requestedScope === "team") {
		return availableScopes.includes(requestedScope) ? [requestedScope] : [];
	}

	return availableScopes;
}

export async function searchStorageRecords({
	query = "",
	filters = {},
	page = 0,
	hitsPerPage = DEFAULT_HITS_PER_PAGE
} = {}) {
	const keyInfo = await getKeyInfo();
	const algoliaFilters = buildSearchFilters(filters);
	const scopes = getSearchScopes(keyInfo, filters.scope);
	const scopedHitsPerPage = Math.max(1, Math.ceil(hitsPerPage / Math.max(scopes.length, 1)));
	const responses = await Promise.all(scopes.map((scope) => {
		const client = searchClients.get(scope);
		return client.searchSingleIndex({
			indexName: keyInfo.indexName,
			searchParams: {
				query,
				page,
				hitsPerPage: scopedHitsPerPage,
				filters: algoliaFilters || undefined
			}
		});
	}));
	const records = await filterStorageSearchRecords(responses.flatMap((response) => (
		response.hits.map(mapHitToRecord)
	)));

	return {
		records,
		page,
		nbPages: responses.reduce((max, response) => Math.max(max, response.nbPages || 0), 0),
		nbHits: responses.reduce((total, response) => total + (response.nbHits || 0), 0),
		hitsPerPage
	};
}

export function resetStorageSearchKey() {
	cachedKeyInfo = null;
	searchClients = new Map();
}
